import { getTrusteeMemberId, getTrustGrantorIds } from "./trustInfoHelpers";

export const isMemberLinkedToAccount = (account, memberId) => {
  if (!account || !memberId) return false;
  const id = String(memberId);
  if (account.owners_info?.some((o) => o && String(o.id) === id)) return true;
  if (account.beneficiaries?.some((b) => b && String(b.household_member_id) === id)) return true;
  if (account.trusted_contacts?.some((tc) => tc && String(tc.household_member_id) === id)) return true;
  if ((account.trust_info?.trustees || []).some((t) => getTrusteeMemberId(t) === id)) return true;
  if (getTrustGrantorIds(account.trust_info).includes(id)) return true;
  if (String(account.inheritance_info?.decedent_info?.id || "") === id) return true;
  if ((account.business_info?.control_persons || []).some((c) => c && String(c.id) === id)) return true;
  return false;
};

const parseMemberPersonalInfo = (member) => {
  const pi = member?.personal_info;
  if (!pi) return {};
  if (typeof pi === "string") {
    try {
      return JSON.parse(pi);
    } catch {
      return {};
    }
  }
  return pi;
};

export const isDeceasedOrDecedent = (member, accounts) => {
  if (!member) return false;
  if (parseMemberPersonalInfo(member).is_deceased === true) return true;
  const id = member.id;
  if (!id) return false;
  return (Array.isArray(accounts) ? accounts : []).some(
    (account) =>
      String(account?.inheritance_info?.decedent_info?.id || "") === String(id),
  );
};

const isOwnerOfAccount = (account, memberId) =>
  (account?.owners_info || []).some(
    (o) => o && String(o.id) === String(memberId),
  );

export const isBusinessOwnerOrControlPerson = (accounts, memberId) => {
  if (!memberId) return false;
  return (Array.isArray(accounts) ? accounts : []).some((account) => {
    if (String(account?.registration || "").toLowerCase() !== "business")
      return false;
    return (
      isOwnerOfAccount(account, memberId) ||
      (account?.business_info?.control_persons || []).some(
        (c) => c && String(c.id) === String(memberId),
      )
    );
  });
};

export const isSimpleIraOwner = (accounts, memberId) => {
  if (!memberId) return false;
  return (Array.isArray(accounts) ? accounts : []).some(
    (account) =>
      String(account?.registration || "").toLowerCase() === "retirement" &&
      String(account?.type || "").toLowerCase() === "simple ira" &&
      isOwnerOfAccount(account, memberId),
  );
};

const isCustodialAccount = (account) => {
  const reg = String(account?.registration || "").toLowerCase();
  const type = String(account?.type || "").toLowerCase();
  return (
    reg.includes("utma") ||
    reg.includes("ugma") ||
    type.includes("utma") ||
    type.includes("ugma")
  );
};

export const isCustodialAccountOwner = (accounts, memberId) => {
  if (!memberId) return false;
  return (Array.isArray(accounts) ? accounts : []).some(
    (account) => isCustodialAccount(account) && isOwnerOfAccount(account, memberId),
  );
};

const isNonCustodialAccountOwner = (accounts, memberId) => {
  if (!memberId) return false;
  return (Array.isArray(accounts) ? accounts : []).some(
    (account) => !isCustodialAccount(account) && isOwnerOfAccount(account, memberId),
  );
};

/**
 * True for the minor of a custodial account: an owner of a UTMA/UGMA account who
 * does not own any non-custodial account (that owner is the adult custodian).
 * Used to exempt only the minor — not the custodian — from the 18+ DOB rule.
 */
export const isCustodialMinorOwner = (accounts, memberId) =>
  isCustodialAccountOwner(accounts, memberId) &&
  !isNonCustodialAccountOwner(accounts, memberId);

export const getUnmappedMembers = (members, accounts) => {
  const mems = Array.isArray(members) ? members : [];
  const accts = Array.isArray(accounts) ? accounts : [];
  if (!mems.length || !accts.length) return [];
  return mems.filter(
    (member) => !accts.some((account) => isMemberLinkedToAccount(account, member.id)),
  );
};
